import type { FC, ReactNode } from 'react'

import { ArrowRight, Search, Sparkles } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useReveal } from '@/hooks/use-reveal'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

interface Step {
    title: string
    body: string
    mark: ReactNode
}

/**
 * The three-step band: a display label on the left with the hand-drawn red
 * underline used across the site, then the steps as outlined cards with an
 * arrow between them.
 *
 * Each card carries a filled black numeral and one mark, so the sequence
 * reads at a glance without the copy.
 */
const Mark: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => (
    <span
        aria-hidden='true'
        className={cn(
            'flex size-11 shrink-0 items-center justify-center rounded-[10px] border-2 border-ink-900',
            className
        )}
    >
        {children}
    </span>
)

/**
 * THE THREE STEPS ARE THE PLATFORM'S ACTUAL MODEL, and the previous three were
 * not. Step one read "Join our active Telegram community", which describes a
 * single platform-wide group that does not exist — Bazio hosts many
 * marketplaces, each with its own Telegram. Step three read "We review it, you
 * go live", which asserts that a person reads every listing; that is true only
 * of a marketplace whose owner chose manual review, and false for one running
 * on automatic publishing. Neither claim may be restated on a shared surface.
 */
const STEPS: Array<Step> = [
    {
        title: 'Communities connect',
        body: 'Telegram owners connect their group or channel to Bazio.',
        mark: (
            <Mark className='border-transparent'>
                <TelegramGlyph className='size-8 text-ink-900' />
            </Mark>
        )
    },
    {
        title: 'Posts become listings',
        body: 'Bazio identifies sale posts and turns them into structured marketplace listings.',
        mark: (
            <Mark className='border-brand-red'>
                <Sparkles className='size-6 text-brand-red' strokeWidth={2.25} aria-hidden='true' />
            </Mark>
        )
    },
    {
        title: 'Buyers discover',
        body: 'People browse, search and contact sellers from one clean storefront.',
        mark: (
            <Mark className='border-ink-900 bg-ink-900'>
                <Search className='size-6 text-paper' strokeWidth={2.5} aria-hidden='true' />
            </Mark>
        )
    }
]

interface HowItWorksProps {
    className?: string
}

export const HowItWorks: FC<HowItWorksProps> = ({ className }): ReactNode => {
    const { ref, shown } = useReveal<HTMLDivElement>({ threshold: 0.2 })

    return (
        <div
            ref={ref}
            className={cn(
                'grid items-center gap-8 lg:grid-cols-[minmax(180px,240px)_minmax(0,1fr)] lg:gap-10',
                className
            )}
        >
            {/* ---- Label column ---- */}
            <div className='flex flex-col items-start'>
                <h2 className='display-lg text-ink-900'>
                    How it
                    <br />
                    works
                </h2>

                <svg className='mt-1.5 block h-auto w-[min(300px,80%)]' viewBox='0 0 320 30' aria-hidden='true'>
                    <path
                        d='M3 24c58-9 122-15 186-17 43-1 88 0 128 4-40 2-84 3-127 5-63 3-127 9-185 19z'
                        fill='var(--brand-red)'
                    />

                    <path
                        d='M28 29c74-11 156-17 240-17-84 4-165 10-240 17z'
                        fill='var(--brand-red)'
                        opacity='.72'
                    />
                </svg>
            </div>

            {/* ---- Steps ---- */}
            <ol className='grid gap-4 sm:grid-cols-2 lg:flex lg:items-stretch lg:gap-0'>
                {STEPS.map((step, index) => (
                    <li
                        key={step.title}
                        /*
                            Transform and opacity only, fired once by
                            `useReveal`, which reports shown immediately under
                            `prefers-reduced-motion` so the settled state is the
                            default rather than something a visitor must trigger.
                        */
                        className={cn(
                            'flex min-w-0 items-center transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none lg:flex-1',
                            shown ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
                        )}
                        style={{ transitionDelay: shown ? `${index * 90}ms` : '0ms' }}
                    >
                        <div className='flex h-full w-full items-center gap-4 rounded-xl border-2 border-ink-900 bg-paper p-5'>
                            <span className='flex shrink-0 items-center gap-2'>
                                <span className='type-label flex size-11 items-center justify-center rounded-[10px] bg-ink-900 text-lg text-paper'>
                                    {index + 1}
                                </span>

                                {step.mark}
                            </span>

                            <span className='min-w-0'>
                                <h3 className='type-label text-[13px] leading-none text-ink-900'>
                                    {step.title}
                                </h3>

                                <p className='mt-2 text-sm leading-snug text-text-muted'>
                                    {step.body}
                                </p>
                            </span>
                        </div>

                        {index < STEPS.length - 1 && (
                            <ArrowRight
                                className='mx-3 hidden size-6 shrink-0 text-ink-900 lg:block'
                                strokeWidth={1.75}
                                aria-hidden='true'
                            />
                        )}
                    </li>
                ))}
            </ol>
        </div>
    )
}
